import { LoadingState } from './components/ui/Primitives';
// src/App.js
import React, { useState, useEffect, useMemo } from 'react';
import './App.css';
import jwt from 'jwt-decode';
import axios from 'axios';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';

import Home from './pages/Home';
import MissedCalls from './pages/MissedCalls';
import SignUp from './pages/SignUp';
import Plans from './pages/Plans';
import OnboardingRedesign from './pages/OnboardingRedesign';
import PhoneNumberSetupRedirect from './components/PhoneNumberSetupRedirect';
import ThanksForSigningUp from './pages/ThanksWait';
import Dashboard from './pages/Dashboard';
import Settings from './pages/Settings';
import Organization from './pages/Organization';
import Transcripts from './pages/Transcripts';
import VerifyAccount from './pages/VerifyAccount';
import Notifications from './pages/Notifications';
import KnowledgeBase from './pages/KnowledgeBase';
import Admin from './pages/Admin';
import Login from './pages/Login';
import Account from './pages/Account';
import { AccountDeletionStatus } from './components/AccountDeletion';
import AcceptOrganizationInvitation from './pages/AcceptOrganizationInvitation';
import Header from './components/Header';
import Footer from './components/Footer';
import PasswordResetStart from './pages/PasswordResetStart';
import PasswordReset from './pages/PasswordReset';
import PrivacyPolicy from './pages/PrivacyPolicy';
import AdminMetrics from './pages/AdminMetrics';
import AccountCleanup from './pages/AccountCleanup';
import CompInvitations from './pages/CompInvitations';
import FAQ from './pages/FAQ';
import Feedback from './pages/Feedback';
import Sidebar from './components/Sidebar';
import Demo from './pages/Demo';
import News from './pages/News';
import CRMIntegrations from './pages/CRMIntegrations';
import { isTransportSafeAuthToken, logEvent } from './utils/logger';
import ManageOrgMembersSection from './components/SettingsComponents/ManageOrgMembersSection';
import LegacyRouteRedirect from './components/LegacyRouteRedirect';
import { LEGACY_ROUTE_REDIRECTS, ROUTES } from './components/navigation';
import FrontendObservability from './components/FrontendObservability';
import MetaPageTracking from './components/MetaPageTracking';
import ToastViewport from './components/ui/ToastViewport';
import { loadSession, isInvalidSession } from './utils/sessionData';
import { isFocusedOnboardingPath } from './utils/appShell';
import { callReturnToFromSearch, signInPath } from './utils/callLinks';
import SelectMenuEnhancer from './components/ui/SelectMenuEnhancer';

function App() {
  return (
    <Router>
      <MainApp />
    </Router>
  );
}

function isEmailEntryPath(pathname) {
  return pathname.startsWith('/accept-invitation/') || pathname.startsWith('/verify-account/');
}

function MainApp() {
  const { pathname } = useLocation();
  const isFocusedOnboarding = isFocusedOnboardingPath(pathname);

  const showSidebar = [
    ROUTES.overview,
    ROUTES.knowledge,
    '/organization',
    ROUTES.calls,
    '/notifications',
    ROUTES.receptionist,
    '/account',
    '/updates',
    ROUTES.integrations,
    ROUTES.operations,
  ].some(path => pathname.startsWith(path));

  const chatIframePaths = [
    ROUTES.calls,
    ROUTES.overview,
    ROUTES.knowledge,
    '/organization',
    ROUTES.receptionist,
    '/onboarding',
    '/onboarding-knowledge',
    '/phone-numbers',
    ROUTES.integrations,
  ];

  // Temporary kill-switch: keep chatbot hidden until realtime API issues are resolved.
  const chatWidgetEnabled = false;
  const shouldShowChat = chatWidgetEnabled && chatIframePaths.some(path => pathname.startsWith(path));

  const [orgMember, setOrgMember] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [appReady, setAppReady] = useState(false);

  const [sessionError, setSessionError] = useState('');
  const [sessionAttempt, setSessionAttempt] = useState(0);

  // auth init
  useEffect(() => {
    let active = true;
    setAppReady(false);
    setSessionError('');
    const token = localStorage.getItem('jwt_authorization');
    if (!token) {
      if (window.location.pathname === ROUTES.calls) window.location.replace(signInPath());
      setAppReady(true);
      return;
    }
    let decoded;
    try {
      decoded = isTransportSafeAuthToken(token) ? jwt(token) : null;
    } catch (_error) {
      decoded = null;
    }
    if (!decoded || !Number.isFinite(decoded.exp) || decoded.exp < Date.now() / 1000) {
      localStorage.removeItem('jwt_authorization');
      setOrgMember(null);
      setAppReady(true);
      if (window.location.pathname !== '/signin' && !isEmailEntryPath(window.location.pathname)) window.location.replace(signInPath());
      return;
    } else {

      
      // fetch organization details
      async function fetchOrgData() {
        try {
          const { member: nextMember, organization: nextOrganization, token: refreshedToken } = await loadSession(axios, process.env.REACT_APP_BACKEND_HOST, token);
          if (!active) return;
          if (isTransportSafeAuthToken(refreshedToken)) localStorage.setItem('jwt_authorization', refreshedToken);
          setOrganization(nextOrganization);
          setOrgMember({
            ...decoded,
            ...nextMember,
            organization: nextOrganization,
            business_name: nextOrganization?.business_name,
          });
        } catch (err) {
          if (!active) return;
          if (isInvalidSession(err)) {
            localStorage.removeItem('jwt_authorization');
            setOrgMember(null);
            if (window.location.pathname !== '/signin' && !isEmailEntryPath(window.location.pathname)) window.location.replace(signInPath());
            return;
          }

          setSessionError(err.response?.status === 409
            ? 'Your organization is unavailable. Please contact support.'
            : 'We could not load your account. Please try again.');
        } finally {
          if (active) setAppReady(true);
        }
      }
      
      fetchOrgData();
    }

    return () => { active = false; };
  }, [sessionAttempt]);

  // remove the #chat-hide-text element on pages without chat
  useEffect(() => {
    if (!shouldShowChat) {
      const el = document.getElementById('chat-hide-text');
      if (el) el.remove();
    }
  }, [shouldShowChat]);

  const logout = () => {
    logEvent({ event: { label: 'Click', data: { value: 'Logout' } } });
    setOrgMember(null);
    localStorage.removeItem('jwt_authorization');
    window.location.href = '/signin';
  };

  const login = (jwt_token, onboard) => {
    let decoded;
    try {
      decoded = isTransportSafeAuthToken(jwt_token) ? jwt(jwt_token) : null;
    } catch (_error) {
      decoded = null;
    }
    if (!decoded || !Number.isFinite(decoded.exp)) {
      localStorage.removeItem('jwt_authorization');
      window.location.replace(signInPath());
      return;
    }
    setOrgMember(decoded);
    localStorage.setItem('jwt_authorization', jwt_token);
    window.location.href = onboard ? '/onboarding' : (callReturnToFromSearch(window.location.search) || ROUTES.overview);
  };

  function checkLogin() {
    const token = localStorage.getItem('jwt_authorization');
    if (!token) return false;
    let decoded;
    try {
      decoded = isTransportSafeAuthToken(token) ? jwt(token) : null;
    } catch (_error) {
      decoded = null;
    }
    if (!decoded || !Number.isFinite(decoded.exp) || decoded.exp < Date.now() / 1000) {
      localStorage.removeItem('jwt_authorization');
      if (window.location.pathname !== '/signin' && !isEmailEntryPath(window.location.pathname)) window.location.replace(signInPath());
      return false;
    }
    return decoded;
  }

  function PasswordResetHandler() {
    const { token } = useParams();
    return token ? <PasswordReset /> : <PasswordResetStart />;
  }

  const chatWidgetHost =
    process.env.REACT_APP_CHAT_WIDGET_HOST ||
    (process.env.NODE_ENV === 'development' ? '/api' : process.env.REACT_APP_BACKEND_HOST);
  const chatWidgetLogHost = process.env.REACT_APP_BACKEND_HOST;

  const chatIframe = useMemo(() => (
    <iframe
      id="chat-iframe"
      title="Alex AI copilot"
      width={500}
      height={550}
      src={`/chat-widget/chat-widget.html?host=${encodeURIComponent(chatWidgetHost)}&logHost=${encodeURIComponent(chatWidgetLogHost)}`}
      style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        transform: 'translateX(520px)',
        transition: 'transform 0.4s ease',
        zIndex: 9998,
        border: 'none',
      }}
    />
  ), [chatWidgetHost, chatWidgetLogHost]);

  return (
    <>
    <FrontendObservability axiosClient={axios} />
    <MetaPageTracking />
    <SelectMenuEnhancer />
    <div className={`App ${showSidebar ? 'say-app-authenticated' : 'say-app-public'}`}>
      <div className={`container${showSidebar ? ' has-sidebar' : ''}`}>
        {!isFocusedOnboarding && <Header checkLogin={checkLogin} logout={logout} orgMember={orgMember} appView={showSidebar} />}
        {showSidebar && <Sidebar checkLogin={checkLogin} orgMember={orgMember} />}

        <ToastViewport />

        <main className="app-main">
        {!appReady ? <LoadingState label="Loading your account..." page /> : sessionError ? (
          <div role="alert"><p>{sessionError}</p><button onClick={() => setSessionAttempt(value => value + 1)}>Try again</button> <button onClick={logout}>Sign out</button></div>
        ) : <Routes>
          <Route path="/" element={<Home checkLogin={checkLogin} orgMember={orgMember} />} />
          <Route path="/missed-calls" element={<MissedCalls />} />
          <Route path={`${ROUTES.operations}/metrics`} element={<AdminMetrics checkLogin={checkLogin} orgMember={orgMember} />} />
          <Route path={`${ROUTES.operations}/account-cleanup`} element={<AccountCleanup checkLogin={checkLogin} orgMember={orgMember} />} />
          <Route path={`${ROUTES.operations}/tester-invitations`} element={<CompInvitations checkLogin={checkLogin} />} />
          <Route path="/manage-org-demo" element={<ManageOrgMembersSection orgMember={orgMember} />} />
          <Route path="/signup" element={<SignUp checkLogin={checkLogin} orgMember={orgMember} login={login} />} />
          <Route path={ROUTES.pricing} element={<Plans checkLogin={checkLogin} orgMember={orgMember} />} />
          <Route
            path="/verify-account/:verifyToken"
            element={<VerifyAccount checkLogin={checkLogin} login={login} />}
          />
          <Route path="/verify-account" element={<VerifyAccount checkLogin={checkLogin} />} />
          <Route
            path="/onboarding-knowledge"
            element={<Navigate to="/onboarding" replace />}
          />
          <Route
            path="/onboarding-customize-ai"
            element={<Navigate to="/onboarding" replace />}
          />
          <Route path="/signin" element={<Login handleLogin={login} />} />
          <Route path="/accept-invitation/:invitationToken" element={<AcceptOrganizationInvitation login={login} />} />
          <Route
            path="/onboarding"
            element={<OnboardingRedesign />}
          />
          <Route path={ROUTES.help} element={<FAQ />} />
          <Route path="/feedback/:surveyId?" 
                 checkLogin={checkLogin} 
                 element={<Feedback />} />
          <Route path="/deletion-status" element={<AccountDeletionStatus />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/password-reset/:token?" element={<PasswordResetHandler />} />
          <Route
            path="/thanks-for-signing-up"
            element={<ThanksForSigningUp checkLogin={checkLogin} orgMember={orgMember} />}
          />
          <Route path={ROUTES.overview} element={<Dashboard checkLogin={checkLogin} orgMember={orgMember} organization={organization} />} />
          <Route
            path={ROUTES.knowledge}
            element={<KnowledgeBase checkLogin={checkLogin} organization={organization} orgMember={orgMember} />}
          />
          <Route
            path="/phone-numbers"
            element={<PhoneNumberSetupRedirect />}
          />
          <Route path={ROUTES.calls} element={<Transcripts checkLogin={checkLogin} organization={organization} orgMember={orgMember} />} />
          <Route
            path="/organization"
            element={<Organization checkLogin={checkLogin} organization={organization} orgMember={orgMember} />}
          />
          <Route
            path="/notifications"
            element={<Notifications orgMember={orgMember} />}
          />
          <Route
            path={ROUTES.receptionist}
            element={<Settings checkLogin={checkLogin} organization={organization} orgMember={orgMember} />}
          />
          <Route
            path={ROUTES.integrations}
            element={<CRMIntegrations checkLogin={checkLogin} orgMember={orgMember} organization={organization} />}
          />
          <Route path="/account" element={<Account />} />
          <Route path="/demo" element={<Demo />} />
          <Route path={ROUTES.operations} element={<Admin checkLogin={checkLogin} orgMember={orgMember} />} />
          <Route
            path="/updates/:slug?"
            element={<News checkLogin={checkLogin} orgMember={orgMember} />}
          />
          {LEGACY_ROUTE_REDIRECTS.map(({ from, to, oauthTo }) => (
            <Route key={from} path={`${from}/*`} element={<LegacyRouteRedirect from={from} to={to} oauthTo={oauthTo} />} />
          ))}
          <Route path="*" element={<Home checkLogin={checkLogin} orgMember={orgMember} />} />
        </Routes>}
        </main>
        {!showSidebar && !isFocusedOnboarding && <Footer />}
      </div>

      {appReady && shouldShowChat && chatIframe}
    </div>
    </>
  );
}

export default App;
